import { BUILTIN_DASHBOARDS, BUILTIN_REPORTS } from "./catalog";
import { CONTRACTS } from "./data-contracts";
import modelText from "./malloy/luuta.malloy?raw";
import { tx, type Locale } from "./i18n";
import type {
  QueryDefinition,
  ResultField,
  VisualizationConfig,
} from "./types";

type Named = {
  id: string;
  builtin?: boolean;
  name: string;
  description: string;
};
const templates = new Map(
  [...BUILTIN_REPORTS, ...BUILTIN_DASHBOARDS].map((item) => [item.id, item]),
);
const reportTemplates = new Map(BUILTIN_REPORTS.map((item) => [item.id, item]));
function template(item: Named) {
  return item.builtin ? templates.get(item.id) : undefined;
}
/** Translate untouched defaults at display time; never rewrite saved metadata. */
export function displayName(item: Named) {
  return item.name === template(item)?.name ? tx(item.name) : item.name;
}
export function displayDescription(item: Named) {
  return item.description === template(item)?.description
    ? tx(item.description)
    : item.description;
}
export function displayTag(report: QueryDefinition, tag: string) {
  const original = report.builtin && reportTemplates.get(report.id);
  return original && original.tags.includes(tag) ? tx(tag) : tag;
}
export function displayAuthor(author: { id: string; name: string }) {
  return author.id === "demo-manager" &&
    author.name === "Luuta Operations Manager"
    ? tx(author.name)
    : author.name;
}
export const readable = (name: string) =>
  name.replaceAll("_", " ").replace(/^./, (c) => c.toUpperCase());
export function catalogText(text: string): string {
  const translated = tx(text);
  if (translated !== text) return translated;
  for (const prefix of ["Luuta data model:", "Synthetic TEHO extension:"]) {
    if (text.startsWith(prefix + " "))
      return tx(prefix) + " " + tx(text.slice(prefix.length + 1));
  }
  const join = text.indexOf(": ");
  return join > 0
    ? tx(text.slice(0, join)) + ": " + tx(text.slice(join + 2))
    : tx(text);
}
const fieldLabels = new Map<string, Set<string>>();
function register(name: string, label = readable(name)) {
  const labels = fieldLabels.get(name) ?? new Set<string>();
  labels.add(label);
  fieldLabels.set(name, labels);
}
for (const contract of Object.values(CONTRACTS)) {
  for (const name of Object.keys(contract.columns)) register(name);
}
let annotation: string | undefined;
for (const line of modelText.split("\n")) {
  const label = line.match(/\blabel\s*=\s*"([^"]*)"/);
  if (label) annotation = label[1];
  if (/^source:/.test(line)) {
    annotation = undefined;
    continue;
  }
  const field = line.match(
    /^\s*(?:(?:dimension|measure|group_by|aggregate|nest|view):\s*)?([a-zA-Z_]\w*)(?:\s+is\b|\s*$)/,
  );
  if (field) {
    register(field[1]);
    if (annotation) register(field[1], annotation);
    annotation = undefined;
  }
}
for (const report of BUILTIN_REPORTS) {
  if (report.mode === "malloy") {
    for (const alias of report.malloy.matchAll(/\bnest:\s*(\w+)\s+is\b/g))
      register(alias[1]);
  }
  for (const [name, label] of Object.entries(
    report.visualization.fieldLabels ?? {},
  ))
    register(name, label);
}
const relationships = [
  "sessions",
  "task_inspections",
  "orders",
  "allocations",
  "priced_task",
  "requests",
  "trips",
  "waits",
];
export function resultLabel(
  field: Pick<ResultField, "name" | "label">,
  override?: string,
  literal = false,
) {
  if (literal) return override ?? field.label;
  const name = field.name.replaceAll(".", "_");
  const labels = fieldLabels.get(name);
  const value = override ?? field.label;
  if (labels?.has(value)) return tx(value);
  // Only known model relationships may contribute a translated prefix.
  for (const relationship of relationships) {
    if (!name.startsWith(relationship + "_")) continue;
    const label = value.slice(value.indexOf(": ") + 2);
    if (
      value.startsWith(readable(relationship) + ": ") &&
      fieldLabels.get(name.slice(relationship.length + 1))?.has(label)
    )
      return catalogText(value);
  }
  return value;
}
export function fieldTitle(
  name: string,
  fields: ResultField[],
  override?: string,
  literal = false,
) {
  return resultLabel(
    fields.find((field) => field.name === name) ?? {
      name,
      label: readable(name),
    },
    override,
    literal,
  );
}
/** Distinguish generated labels from intentional overrides, even when an override is another translated term. */
export function literalFieldLabels(
  config: Pick<VisualizationConfig, "fieldLabels">,
  fields: ResultField[],
  report?: Named,
) {
  const original =
    report && report.builtin
      ? reportTemplates.get(report.id)?.visualization.fieldLabels
      : undefined;
  return new Set(
    Object.entries(config.fieldLabels ?? {})
      .filter(([name, label]) => {
        const field = fields.find((field) => field.name === name);
        return label !== field?.label && label !== original?.[name];
      })
      .map(([name]) => name),
  );
}
const statusFields = new Set([
  "status",
  "cleaning_state",
  "outcome",
  "first_outcome",
  "supply_state",
  "priority",
]);
const statusLabels: Record<string, string> = {
  BLOCKED: "Blocked",
  READY: "Ready",
  CLEAN: "Clean",
  DIRTY: "Dirty",
  IN_PROGRESS: "In progress",
  INSPECTION: "Inspection",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
  SUBMITTED: "Submitted",
  FULFILLED: "Fulfilled",
  PENDING: "Pending",
  SCHEDULED: "Scheduled",
  STARTED: "Started",
  PAUSED: "Paused",
  FAILED: "Failed",
  PASSED: "Passed",
  NORMAL: "Normal",
  HIGH: "High",
  LOW: "Low",
  URGENT: "Urgent",
  ACTIVE: "Active",
  REVIEWED: "Reviewed",
  SENT: "Sent",
  STOCKED: "Stocked",
  REGULAR: "Regular",
};
export function statusText(value: string, field: string, locale?: Locale) {
  const name = field.split(".").at(-1) ?? field;
  const relationship = relationships.find((prefix) =>
    name.startsWith(prefix + "_"),
  );
  const canonical = relationship ? name.slice(relationship.length + 1) : name;
  const label = statusLabels[value.toUpperCase()];
  return statusFields.has(canonical) && label ? tx(label, {}, locale) : value;
}
